import { forwardRef, memo, useEffect, useMemo, useRef } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { DisclosureNext } from '@sas/react-svg';
import cx from 'classnames';
import { transformTokensToCSS } from '../__internal__';
import { useHighlightableText } from '../../../hooks/useHighlightableText';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { Button, ButtonVariant } from '../../Button';
import { UnthemedCheckbox } from '../../Checkbox';
import { Icon } from '../../Icon';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';
import { UnthemedRadioButton } from '../../RadioButton/__internal__';
import bundle from '../NovaComponents-ListBox-gui-icu.properties';
import '../List.css';
import cn from '../List.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const getIndentation = indented => typeof indented === 'boolean' ? indented ? 1 : 0 : indented;
export const ListItem = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(function ListItem(props, ref) {
  const localize = useTextLocalizer();
  const refLi = useRef(null);
  const textRef = useTruncationTooltip();
  const secondaryTextRef = useTruncationTooltip();
  const dt = useDesignTokens();
  const tokens = transformTokensToCSS(props.index, props.tokens);
  const indentationLevel = getIndentation(props.indented);
  const getIconA11yInfo = useIconA11yInfo();
  useEffect(() => {
    props.focused && refLi.current?.focus();
  }, [props.focused]);
  const displayMode = props.displayMode;
  const className = cx(props.className, cn.item, props.navigation && cn.navigationItem, {
    [cn.itemHoverable]: !props.selected && displayMode !== 'checkbox' && displayMode !== 'radio',
    [cn.selected]: props.selected && displayMode !== 'checkbox' && displayMode !== 'radio'
  }, props.focused && cn.focused, props.showSelectedPseudoFocus && cn.pseudoFocused, props.indented && cn.indented, props.disabled && cn.disabled, props.showSeparator && cn.itemTopSeparator);
  const selectedIndicator = displayMode === 'checkbox' ? /*#__PURE__*/_jsx("span", {
    "aria-hidden": "true",
    children: /*#__PURE__*/_jsx(UnthemedCheckbox, {
      checked: props.selected,
      focusable: false,
      className: cn.itemSelectionControl,
      onPress: () => {
        if (props.disabled) return;
        props.onSelected?.({
          index: props.index
        });
      },
      disabled: props.disabled
    })
  }) : displayMode === 'radio' ? /*#__PURE__*/_jsx("span", {
    "aria-hidden": "true",
    children: /*#__PURE__*/_jsx(UnthemedRadioButton, {
      selected: props.selected,
      className: cn.itemSelectionControl,
      disabled: props.disabled
    })
  }) : null;
  const showHyphen = props.concatSecondaryText && props.secondaryText;
  const text = useHighlightableText({
    text: props.text ?? '',
    className: cx(cn.highlight, props.highlightableConfig?.text?.className),
    ...props.highlightableConfig?.text
  });
  const secondaryHighlightableText = useHighlightableText({
    text: props.secondaryText ?? '',
    className: cx(cn.highlight, props.highlightableConfig?.secondaryText?.className),
    ...props.highlightableConfig?.secondaryText
  });
  const secondaryText = props.secondaryText ? /*#__PURE__*/_jsx("span", {
    "data-testid": postfixTestId(props.testId, 'secondaryText'),
    className: cn.itemSecondaryText,
    ref: secondaryTextRef,
    children: secondaryHighlightableText
  }) : null;
  const itemTextSeparator = '-';
  const iconProps = useMemo(() => {
    if (!props.icon) return;
    const element = props.icon.render({}, undefined);
    return {
      testId: postfixTestId(props.testId, 'icon'),
      icon: props.icon,
      tooltip: props.iconTooltip,
      ariaLabel: props.iconAriaLabel,
      ariaAriaHidden: props.iconAriaHidden,
      style: {
        width: `${element.props.width}px`,
        height: `${element.props.height}px`
      }
    };
  }, [props.icon, props.iconAriaHidden, props.iconAriaLabel, props.iconTooltip, props.testId]);
  const ariaLabel = useMemo(() => {
    const label = [];
    // level
    if (!isNullish(indentationLevel)) label.push(localize('indentation.level.msg', {
      level: `${indentationLevel + 1}`
    }));

    // icon
    const getIconDescription = icon => {
      const {
        ariaLabel,
        tooltip
      } = getIconA11yInfo({
        ...icon
      });
      return ariaLabel ?? tooltip;
    };
    const accessibleIcon = iconProps && !iconProps.ariaAriaHidden ? getIconDescription(iconProps) : undefined;
    if (accessibleIcon) label.push(accessibleIcon);

    // accessible text
    if (props.ariaLabel) {
      label.push(props.ariaLabel);
    } else {
      if (props.text) label.push(props.text);
      if (props.secondaryText) label.push(props.secondaryText);
    }

    // tooltip
    if (props.tooltip) label.push(props.tooltip);
    return label.length !== 0 ? label.join(', ') : undefined;
  }, [getIconA11yInfo, iconProps, indentationLevel, localize, props.ariaLabel, props.secondaryText, props.text, props.tooltip]);
  return /*#__PURE__*/_jsx("li", {
    id: props.id,
    role: "option",
    tabIndex: props.focusable ? 0 : -1,
    "aria-label": ariaLabel,
    "aria-selected": !!props.selected,
    "aria-disabled": props.disabled,
    "aria-setsize": props['aria-setsize'],
    "aria-posinset": props['aria-posinset'],
    title: props.tooltip,
    onClick: event => {
      if (props.disabled) {
        event.stopPropagation();
        event.preventDefault();
        return;
      }
      // HACK: Button calls preventDefault on onClick events
      if (!event.defaultPrevented) {
        props.onSelected?.({
          index: props.index
        });
        event.preventDefault();
      }
    },
    onKeyDown: event => {
      if (props.disabled && (event.key === 'Enter' || event.key === ' ')) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    onFocus: props.onFocus,
    className: className,
    ref: element => {
      setRef(ref, element);
      refLi.current = element;
    },
    "data-testid": props.testId,
    style: {
      ...tokens,
      ...renderInlineCssVars({
        'indentation-level': Math.max(0, !isNullish(indentationLevel) ? indentationLevel : 0)
      }, 'list-item'),
      ...props.style
    },
    children: /*#__PURE__*/_jsxs("div", {
      className: cn.itemInner,
      children: [selectedIndicator, iconProps && /*#__PURE__*/_jsx(Icon, {
        ...iconProps,
        className: cn.itemIcon,
        tokens: () => {
          return {
            fg: props.selected && dt.themeVariant === 'high-contrast' ? 'var(--list-item-selected-fg)' : undefined
          };
        },
        disabled: props.disabled
      }), /*#__PURE__*/_jsx("span", {
        className: cn.itemText,
        "data-testid": postfixTestId(props.testId, 'text'),
        ref: textRef,
        children: text
      }), showHyphen ? /*#__PURE__*/_jsx("span", {
        "aria-hidden": true,
        className: cn.itemTextSeparator,
        children: itemTextSeparator
      }) : null, props.concatSecondaryText ? secondaryText : null, /*#__PURE__*/_jsx("div", {
        className: cn.itemSpacer
      }), !props.concatSecondaryText ? secondaryText : null,
      // note this is deprecated but kept here for backward compatibility
      props.actionIcon && /*#__PURE__*/_jsx(Button, {
        className: cn.itemActionIcon,
        variant: ButtonVariant.Icon,
        onPress: () => {
          if (props.disabled) return;
          props.onActionPress?.({
            index: props.index
          });
        },
        focusable: false,
        icon: {
          className: cn.itemIcon,
          icon: props.actionIcon
        }
      }), props.navigation && /*#__PURE__*/_jsx(Icon, {
        className: cn.navigationIcon,
        icon: DisclosureNext,
        disabled: props.disabled
      })]
    })
  });
}));
//# sourceMappingURL=ListItem.js.map